import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { 
  CheckCircle, 
  XCircle, 
  Loader2, 
  ArrowLeft, 
  Home,
  Mail,
  FileText,
  Clock
} from 'lucide-react';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PayphoneConfirmacionPage = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading'); // loading, success, error, cancelled
  const [transactionData, setTransactionData] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const confirmTransaction = async () => {
      const id = searchParams.get('id');
      const clientTransactionId = searchParams.get('clientTransactionId');

      if (!id || !clientTransactionId) {
        setStatus('error');
        setErrorMessage('Parámetros de transacción no válidos');
        return;
      }

      try {
        // Call our backend to confirm with Payphone and send email
        const response = await axios.post(`${BACKEND_URL}/api/payphone/confirm`, {
          id: parseInt(id),
          clientTransactionId: clientTransactionId
        });

        const data = response.data;

        if (data.transactionStatus === 'Approved' || data.statusCode === 3) {
          setStatus('success');
          setTransactionData(data);
        } else if (data.statusCode === 2) {
          setStatus('cancelled');
          setTransactionData(data);
        } else {
          setStatus('error');
          setErrorMessage(data.message || 'La transacción no fue aprobada');
          setTransactionData(data);
        }
      } catch (error) {
        console.error('Error confirming transaction:', error);
        setStatus('error');
        setErrorMessage(error.response?.data?.detail || 'Error al confirmar la transacción');
      }
    };

    confirmTransaction();
  }, [searchParams]);

  const renderContent = () => {
    switch (status) {
      case 'loading':
        return (
          <div className="text-center py-12">
            <Loader2 className="w-16 h-16 mx-auto text-[#4C8C3B] animate-spin mb-4" />
            <h2 className="text-xl font-semibold text-gray-700">Verificando pago...</h2>
            <p className="text-gray-500 mt-2">Por favor espere mientras confirmamos su transacción</p>
          </div>
        );

      case 'success':
        return (
          <div className="text-center py-8">
            <div className="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-6">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-green-700 mb-2">¡Pago Exitoso!</h2>
            <p className="text-gray-600 mb-6">Su transacción ha sido procesada correctamente</p>

            {/* Transaction Details */}
            <div className="bg-gray-50 rounded-xl p-6 text-left max-w-md mx-auto mb-6">
              <h3 className="font-semibold text-gray-700 mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Detalles de la Transacción
              </h3>
              <div className="space-y-3 text-sm">
                {transactionData?.transactionId && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">ID Transacción:</span>
                    <span className="font-mono font-medium">{transactionData.transactionId}</span>
                  </div>
                )}
                {transactionData?.authorizationCode && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Código Autorización:</span>
                    <span className="font-mono font-medium">{transactionData.authorizationCode}</span>
                  </div>
                )}
                {transactionData?.amount && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Monto:</span>
                    <span className="font-bold text-green-600">
                      ${(transactionData.amount / 100).toFixed(2)} {transactionData.currency || 'USD'}
                    </span>
                  </div>
                )}
                {transactionData?.cardBrand && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Tarjeta:</span>
                    <span className="font-medium">{transactionData.cardBrand}</span>
                  </div>
                )}
                {transactionData?.lastDigits && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Terminación:</span>
                    <span className="font-mono">****{transactionData.lastDigits}</span>
                  </div>
                )}
                {transactionData?.date && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Fecha:</span>
                    <span className="font-medium">
                      {new Date(transactionData.date).toLocaleString('es-EC')}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Invoice Notice */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 max-w-md mx-auto mb-6">
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-blue-600 mt-0.5" />
                <div className="text-left">
                  <p className="font-medium text-blue-800">Factura en camino</p>
                  <p className="text-sm text-blue-600">
                    Pronto estaremos enviando su factura al correo electrónico registrado. 
                    Si tiene alguna consulta, contáctenos a finanzas@analytica-ec.com
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/">
                <Button className="bg-[#4C8C3B] hover:bg-[#3d7030] text-white">
                  <Home className="w-4 h-4 mr-2" />
                  Volver al Inicio
                </Button>
              </Link>
            </div>
          </div>
        );

      case 'cancelled':
        return (
          <div className="text-center py-8">
            <div className="w-20 h-20 mx-auto bg-yellow-100 rounded-full flex items-center justify-center mb-6">
              <Clock className="w-12 h-12 text-yellow-600" />
            </div>
            <h2 className="text-2xl font-bold text-yellow-700 mb-2">Pago Cancelado</h2>
            <p className="text-gray-600 mb-6">La transacción fue cancelada o no se completó</p>
            
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/payphone">
                <Button className="bg-[#2F3D8E] hover:bg-[#243070] text-white">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Intentar Nuevamente
                </Button>
              </Link>
              <Link to="/">
                <Button variant="outline">
                  <Home className="w-4 h-4 mr-2" />
                  Ir al Inicio
                </Button>
              </Link>
            </div>
          </div>
        );

      case 'error':
      default:
        return (
          <div className="text-center py-8">
            <div className="w-20 h-20 mx-auto bg-red-100 rounded-full flex items-center justify-center mb-6">
              <XCircle className="w-12 h-12 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-red-700 mb-2">Error en el Pago</h2>
            <p className="text-gray-600 mb-4">{errorMessage || 'No se pudo procesar la transacción'}</p>
            
            {transactionData?.message && (
              <p className="text-sm text-red-500 mb-6">{transactionData.message}</p>
            )}

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/payphone">
                <Button className="bg-[#2F3D8E] hover:bg-[#243070] text-white">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Intentar Nuevamente
                </Button>
              </Link>
              <Link to="/">
                <Button variant="outline">
                  <Home className="w-4 h-4 mr-2" />
                  Ir al Inicio
                </Button>
              </Link>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#2F3D8E] via-[#3d4d9e] to-[#4C8C3B] flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#2F3D8E] to-[#4C8C3B] p-4 flex items-center justify-center">
          <img 
            src="https://customer-assets.emergentagent.com/job_3ee71ab0-856c-41a8-8e43-f382a70126f5/artifacts/3okyucn0_LOGO%20ANALYTICA.png" 
            alt="Analytica" 
            className="h-10"
          />
        </div>

        {/* Content */}
        <div className="p-6">
          {renderContent()}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 text-center">
          <p className="text-xs text-gray-500">
            Pago procesado de forma segura por Payphone
          </p>
        </div>
      </div>
    </div>
  );
};

export default PayphoneConfirmacionPage;
